import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes/public";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";

/** Landing-page FAQ entries first, then the FAQ-page-only ones. */
const FAQ_KEYS: ReadonlyArray<{ q: string; a: string }> = [
  { q: "landing.faq.q1", a: "landing.faq.a1" },
  { q: "landing.faq.q2", a: "landing.faq.a2" },
  { q: "landing.faq.q3", a: "landing.faq.a3" },
  { q: "landing.faq.q4", a: "landing.faq.a4" },
  { q: "landing.faq.q5", a: "landing.faq.a5" },
  { q: "faq.items.baptism.q", a: "faq.items.baptism.a" },
  { q: "faq.items.rsvpCutoff.q", a: "faq.items.rsvpCutoff.a" },
  { q: "faq.items.password.q", a: "faq.items.password.a" },
  { q: "faq.items.hideRegistry.q", a: "faq.items.hideRegistry.a" },
];

/** JUB-100: public FAQ page, linked from the footers. */
export default function FaqPage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();

  usePageMeta({
    title: t("faq.metaTitle"),
    description: t("faq.metaDescription"),
    locale,
    path: PUBLIC_ROUTES.faq(locale),
    alternates: { ro: PUBLIC_ROUTES.faq("ro"), en: PUBLIC_ROUTES.faq("en") },
  });

  return (
    <PublicContentLayout>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">{t("faq.title")}</h1>
      <p className="mt-3 text-muted-foreground">{t("faq.subtitle")}</p>

      <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-white" data-testid="faq-list">
        {FAQ_KEYS.map(({ q, a }) => (
          <details key={q} className="group px-5 py-4" data-testid="faq-item">
            <summary className="cursor-pointer list-none font-medium text-foreground flex items-center justify-between gap-4">
              <span>{t(q)}</span>
              <span className="text-muted-foreground group-open:rotate-180 transition-transform" aria-hidden="true">
                ⌄
              </span>
            </summary>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{t(a)}</p>
          </details>
        ))}
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        {t("faq.moreGuides")}{" "}
        <Link to={PUBLIC_ROUTES.blog(locale)} className="font-medium text-pink-600 hover:text-pink-700">
          {t("faq.moreGuidesLink")}
        </Link>
      </p>
    </PublicContentLayout>
  );
}
